// `field/classification-sets/` API service (axios) — reusable hierarchical
// option trees a Classification field can reference (Fulcrum "App Setup >
// Classification Sets" parity). Mirrors the field/forms service pattern
// (src/services/fieldForms.ts); React Query hooks live in
// src/api/classificationSets.ts.

import { AxiosResponse } from "axios";
import { api } from ".";
import { ClassificationSet, ClassificationSetInput, ClassificationSetType } from "../types/classificationSet";
import { ClassificationOption } from "../types/fieldForm";

const BASE = "v2/field/classification-sets";

/** DRF-style paginated list envelope. */
interface PaginatedResponse<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

/**
 * The backend wraps single-object responses in `{ message, data }` and returns
 * `last_updated_at`. Unwrap the envelope and normalise `updated_at` onto the
 * `ClassificationSet` shape the app expects.
 */
function unwrapSet(body: unknown): ClassificationSet {
  const envelope = body as { data?: unknown } | null;
  const hasEnvelope = !!envelope && typeof envelope === "object" && "data" in envelope && !!envelope.data;
  const raw = (hasEnvelope ? envelope.data : body) as Record<string, unknown>;
  return {
    ...(raw as unknown as ClassificationSet),
    // Older sets predate the `type` field — default to the hierarchical set.
    type: (raw.type as ClassificationSetType) ?? "classification_set",
    updated_at: (raw.updated_at ?? raw.last_updated_at) as string
  };
}

/** List all sets (org-scoped), newest-updated first. Optionally filter by `type`. */
export async function listClassificationSets(type?: ClassificationSetType): Promise<ClassificationSet[]> {
  const res = await api.get<PaginatedResponse<ClassificationSet>>(`${BASE}/`, {
    params: type ? { type } : undefined
  });
  const results = (res.data?.results ?? []).map(unwrapSet);
  return results.sort((a, b) => (b.updated_at ?? "").localeCompare(a.updated_at ?? ""));
}

/** Create (no id → POST) or update (existing id → PATCH) a set. */
export async function saveClassificationSet(input: ClassificationSetInput): Promise<ClassificationSet> {
  const payload = { name: input.name, description: input.description, type: input.type, tree: input.tree };
  const res: AxiosResponse = input.id ? await api.patch(`${BASE}/${input.id}/`, payload) : await api.post(`${BASE}/`, payload);
  return unwrapSet(res.data);
}

/** Delete a set. */
export async function deleteClassificationSet(id: string): Promise<void> {
  await api.delete(`${BASE}/${id}/`);
}

// ── Tree helpers (shared by the editor + capture) ────────────────────────────

/** Depth-first flatten of every node's label (parents + leaves) for a flat mirror. */
export function flattenTreeLabels(tree: ClassificationOption[], acc: string[] = []): string[] {
  for (const node of tree) {
    if (node.label) acc.push(node.label);
    if (node.children?.length) flattenTreeLabels(node.children, acc);
  }
  return acc;
}

/**
 * Depth-first flatten of every node's value-slug (parents + leaves). This is the
 * flat `field.options` mirror materialised onto a Classification field — values,
 * not labels, so captured data uses stable slugs (backend contract §7 Q1).
 */
export function flattenTreeValues(tree: ClassificationOption[], acc: string[] = []): string[] {
  for (const node of tree) {
    const val = node.value || slugifyValue(node.label || "");
    if (val) acc.push(val);
    if (node.children?.length) flattenTreeValues(node.children, acc);
  }
  return acc;
}

/** Slugify a label into a stable data-name value (e.g. "Visitor Monitoring" → "visitor_monitoring"). */
export function slugifyValue(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}
